import type { Highlight } from '../../highlight.class';
import type { HighlightManager } from '../highlight-manager.class';

/**
 * Parameters for animating a highlight in
 */
export interface AnimateHighlightInParams {
	highlight: Highlight;
}

/**
 * Animate a new highlight appearing (no previous highlight)
 *
 * @param self The HighlightManager instance
 * @param params Animation parameters
 */
export const animateHighlightInCfn = (
	self: HighlightManager,
	params: AnimateHighlightInParams
): void => {
	const { highlight } = params;

	// Access fields via public getters
	const animationManager = self.animationManager;
	const animationDuration = self.animationDuration;
	const maxIntensity = self.maxIntensity;

	// Stop any existing animations for this highlight
	animationManager.stop(`highlight-position-${highlight.objectId}`);
	animationManager.stop(`highlight-intensity-${highlight.objectId}`);

	const startPos = highlight.highlightLightSource;
	const endPos = highlight.highlightTarget;
	const distance = startPos.distanceTo(endPos);

	self.animationPhase = 'sliding-forward';

	// If start and end are very close, just set final position and intensity immediately
	if (distance < 0.1) {
		highlight.updateLightPosition(endPos);
		highlight.setIntensity(maxIntensity);
		self.animationPhase = null;
		return;
	}

	// Animate position from source to target
	animationManager.animatePosition(
		`highlight-position-${highlight.objectId}`,
		startPos,
		endPos,
		animationDuration,
		(position) => {
			highlight.updateLightPosition(position);

			// Update intensity based on distance
			const distanceToTarget = position.distanceTo(endPos);
			const totalDistance = startPos.distanceTo(endPos);
			const progress = totalDistance > 0 ? 1 - (distanceToTarget / totalDistance) : 1;
			const intensity = progress * maxIntensity;
			highlight.setIntensity(intensity);
		},
		{
			onComplete: () => {
				highlight.setIntensity(maxIntensity);
				self.animationPhase = null;
			}
		}
	);
};
